import React from "react";
import { useTranslation } from "react-i18next";
import { PencilLine, Trash2 } from "lucide-react";

const AssetGrid = ({ data, onEdit, onDelete }) => {
  const { t } = useTranslation();

  if (!data || data?.length === 0) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-2xl py-10 flex flex-col items-center justify-center text-center">
        <p className="text-sm font-medium text-gray-700">{t("assetManagement.grid.noResults")}</p>
        <p className="text-xs text-gray-500 mt-1">
          {t("assetManagement.grid.noResultsHint")}
        </p>
      </div>
    );
  }

  // same scoring as table
  const levelToScore = (value) => {
    if (!value) return 0;
    const v = String(value).toLowerCase();

    switch (v) {
      case "insignificant":
        return 1;
      case "minor":
        return 2;
      case "moderate":
        return 3;
      case "major":
        return 4;
      case "catastrophe":
        return 5;
      case "unassessed":
        return 0;
      default:
        return 0;
    }
  };

  const getCiaScoreColor = (score) => {
    if (!score) return "text-gray-400";
    if (score >= 75) return "text-red-600";      // high
    if (score >= 40) return "text-amber-600";    // medium
    return "text-emerald-600";                   // low
  };

return (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
    {data.map((asset) => {
      const id = asset.id;

      const name = asset.name ?? t("assetManagement.grid.fallbackUnnamed");
      const category = asset.category ?? t("assetManagement.grid.fallbackUncategorized");
      const location = asset.location ?? t("assetManagement.grid.fallbackUnknownLocation");
      const status = (asset.status ?? "").toLowerCase();
      const sensitivityRaw = asset.sensitivity_level ?? "public";
      const sensitivity = String(sensitivityRaw).toLowerCase();

      const confidentiality = asset.confidentiality;
      const integrity = asset.integrity;
      const availability = asset.availability;

      const ownerFullName =
        asset.first_name && asset.last_name
          ? `${asset.first_name} ${asset.last_name}`
          : t("assetManagement.table.unassigned");

      let statusBadge =
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium";
      let statusLabel = "";
      if (["active", "inactive", "retired", "pending"].includes(status)) {
        statusLabel = t(`assetManagement.status.${status}`);
      } else if (asset.status) {
        statusLabel = asset.status;
      } else {
        statusLabel = t("assetManagement.status.unknown");
      }

      if (status === "active") {
        statusBadge +=
          " bg-green-100 text-green-800 border border-green-200";
      } else if (status === "inactive") {
        statusBadge +=
          " bg-gray-100 text-gray-800 border border-gray-200";
      } else if (status === "retired") {
        statusBadge += " bg-red-100 text-red-800 border-red-200";
      } else if (status === "pending") {
        statusBadge +=
          " bg-yellow-100 text-yellow-800 border-yellow-200";
      } else {
        statusBadge +=
          " bg-slate-100 text-slate-700 border-slate-200";
      }

      const sensitivityChip =
        sensitivity === "public"
          ? "bg-blue-50 text-blue-700 border-blue-100"
          : sensitivity === "internal"
          ? "bg-green-50 text-green-700 border-green-100"
          : sensitivity === "confidential"
          ? "bg-yellow-50 text-yellow-700 border-yellow-100"
          : "bg-red-50 text-red-700 border-red-100";

      const cScore = levelToScore(confidentiality);
      const iScore = levelToScore(integrity);
      const aScore = levelToScore(availability);

      const totalScore =
        cScore > 0 && iScore > 0 && aScore > 0
          ? cScore * iScore * aScore
          : 0;

      const scoreColor = getCiaScoreColor(totalScore);

      const cLabel = cScore || "-";
      const iLabel = iScore || "-";
      const aLabel = aScore || "-";

      return (
        <div
          key={id}
          className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 flex flex-col justify-between hover:border-gray-300 hover:shadow-md transition"
          onClick={() => onEdit(asset)}
        >
          {/* Header: name, category, status */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {name}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {category}
              </p>
            </div>
            <span className={statusBadge}>{statusLabel}</span>
          </div>

          <div className="space-y-2 text-xs text-gray-600 mb-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-400">{t("assetManagement.table.location")}</span>
              <span className="font-medium truncate">{location}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-400">{t("assetManagement.table.department")}</span>
              <span className="font-medium truncate">{asset.department || '-'}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-400">{t("assetManagement.table.owner")}</span>
              <span className="font-medium truncate">
                {ownerFullName}
              </span>
            </div>
            {asset.information_type && (() => {
              let types = [];
              try {
                types = typeof asset.information_type === 'string' 
                  ? JSON.parse(asset.information_type) 
                  : asset.information_type;
              } catch (e) {
                types = [];
              }
              if (Array.isArray(types) && types.length > 0) {
                return (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {types.slice(0, 3).map((type, idx) => (
                      <span key={idx} className="px-2 py-0.5 rounded text-[11px] bg-gray-100 text-gray-700">
                        {type}
                      </span>
                    ))}
                    {types.length > 3 && (
                      <span className="px-2 py-0.5 rounded text-[11px] bg-gray-100 text-gray-700">
                        +{types.length - 3}
                      </span>
                    )}
                  </div>
                );
              }
              return null;
            })()}
            <div className="flex flex-wrap gap-1.5 pt-1">
              <span
                className={
                  "border text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap " +
                  sensitivityChip
                }
              >
                {t("assetManagement.grid.sensitivityLabel", {
                  level: ["public", "internal", "confidential", "restricted"].includes(sensitivity)
                    ? t(`assetManagement.sensitivity.${sensitivity}`)
                    : String(sensitivityRaw || t("incidentManagement.notAvailable")),
                })}
              </span>
            </div>
          </div>

          {/* CIA Score row */}
          <div className="mt-auto border-t border-gray-100 pt-2">
            <div className="mb-2 text-xs">
              <span className={`font-semibold ${scoreColor}`}>
                {totalScore ? `${totalScore}/125` : t("assetManagement.table.unassessed")}
              </span>
              <div className="text-[11px] text-gray-500 mt-0.5">
                C:{cLabel} I:{iLabel} A:{aLabel}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(asset);
                }}
                className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900"
              >
                <PencilLine className="w-3.5 h-3.5" />
                {t("assetManagement.rowActions.edit")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(asset);
                }}
                className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t("assetManagement.rowActions.delete")}
              </button>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

};

export default AssetGrid;
